import { matchPath } from 'react-router';
import { privateRoutes } from '@/routes/Routes.private.config';
import { publicRoutes } from '@/routes/Routes.public.config';
import { serviceRoutes } from '@/routes/Routes.service.config';
import { AllRouteKeysT, RouteT } from '@/routes/Routes.types';

// Вспомогательная функция: получить все ключи роутов (включая вложенные)
export const getAllRouteKeys = (routes: Record<string, RouteT>): string[] => {
    const keys: string[] = [];

    const collectKeys = (r: Record<string, RouteT>): void => {
        Object.keys(r).forEach((key) => {
            keys.push(key);
        });

        // Собираем ключи из subRoutes
        Object.values(r).forEach((route) => {
            if (route.subRoutes) {
                Object.keys(route.subRoutes).forEach((subKey) => {
                    keys.push(subKey);
                });
            }
        });
    };

    collectKeys(routes);
    return [...new Set(keys)]; // Убираем дубликаты
};

export const getRoute = (key: AllRouteKeysT): RouteT => {
    const allRoutes = { ...privateRoutes, ...publicRoutes, ...serviceRoutes };

    const findRouteRecursive = (
        routes: Record<string, RouteT>,
        searchKey: string,
    ): RouteT | undefined => {
        // Ищем в текущем уровне
        if (routes[searchKey]) {
            return routes[searchKey];
        }

        // Рекурсивно ищем в subRoutes каждого роута
        for (const route of Object.values(routes)) {
            if (route.subRoutes) {
                // Ищем в непосредственных subRoutes
                if (route.subRoutes[searchKey]) {
                    return route.subRoutes[searchKey];
                }

                // Рекурсивно ищем вглубь по вложенным subRoutes
                const foundInSubRoutes = findRouteRecursive(
                    route.subRoutes,
                    searchKey,
                );
                if (foundInSubRoutes) {
                    return foundInSubRoutes;
                }
            }
        }

        return undefined;
    };

    const route = findRouteRecursive(allRoutes, key);

    if (!route) {
        const availableKeys = getAllRouteKeys(allRoutes);
        throw new Error(
            `Route with key '${key}' not found. Available keys: ${availableKeys.join(', ')}`,
        );
    }

    return route;
};

// Упрощенная функция: получить все пути роутов
export const getAllRoutePaths = (routes: Record<string, RouteT>): string[] => {
    const paths: string[] = [];

    const collectPaths = (r: Record<string, RouteT>): void => {
        Object.values(r).forEach((route) => {
            paths.push(route.path); // Просто берем path из каждого роута

            if (route.subRoutes) {
                collectPaths(route.subRoutes);
            }
        });
    };

    collectPaths(routes);
    return [...new Set(paths)]; // Убираем дубликаты
};

const pathToRegex = (path: string): RegExp =>
    new RegExp(
        `^${
            path
                .replaceAll('/', String.raw`\/`)
                .replaceAll(/:\w+/g, String.raw`[^\/]+`) // заменяем :id на любой сегмент
        }$`,
    );

// Поиск роута по URL (+ динамические пути)
export const getRouteDataByUrl = (searchedUrl: string): RouteT | null => {
    const allRoutes = { ...privateRoutes, ...publicRoutes, ...serviceRoutes };

    const findRouteByUrl = (
        routes: Record<string, RouteT>,
    ): RouteT | undefined => {
        for (const route of Object.values(routes)) {
            const regex = pathToRegex(route.path);

            if (regex.test(searchedUrl)) {
                return route;
            }

            if (route.subRoutes) {
                const found = findRouteByUrl(route.subRoutes);
                if (found) {
                    return found;
                }
            }
        }
        return undefined;
    };

    const route = findRouteByUrl(allRoutes);

    if (!route) {
        return serviceRoutes.notFound;
    }

    return route;
};

export const checkRouteProperty = (
    routesObject: Record<string, RouteT>,
    pathname: string,
    propertyName: keyof RouteT,
): boolean =>
    Object.values(routesObject).some((route) => {
        const match = matchPath(
            {
                path: route.path,
                end: true,
            },
            pathname,
        );

        if (match && route[propertyName]) {
            return true;
        }

        return !!(
            route.subRoutes &&
            checkRouteProperty(route.subRoutes, pathname, propertyName)
        );
    });

export const path = (
    routeName: string,
    parameter?: string | number | null,
): string => {
    const path = getRoute(routeName).path;
    const match = path.match(/:([A-Za-z0-9_]+)/);

    if (!match) {
        return path;
    }

    const placeholder = match[0];

    return path.replace(placeholder, parameter ? String(parameter) : '');
};
